import { makeAutoObservable, runInAction } from 'mobx';
import i18next, { TOptions } from 'i18next';
import { Trans } from 'react-i18next';

import { LocaleNames, Locales, LocalesToLocaleFileMapping } from '../config';
import { PreferencesStore } from '../stores/PreferencesStore';

/**
 * t() tracks `_i18nReady` — a counter that increments only after i18next has
 * finished loading the new locale file over HTTP. This ensures MobX observer
 * components re-render with the actual translated strings, not before.
 *
 * (Tracking `_locale` instead would re-render immediately, before the HTTP
 * fetch completes, so i18next.t() would still return the old language.)
 */
export const t = (key: string, options?: TOptions) => {
  LocalizationService.i18nReady; // reactive dependency — updates after load
  const translation = i18next.t(key, options);
  return translation ? translation : key;
};

type TranslateProps = {
  i18nKey: string;
  components?: Record<string, React.ReactElement>;
};

export const Translate: React.FC<TranslateProps> = (props) => (
  <Trans {...props} />
);

// Convert an i18next filename ('fi-FI', 'en-US') to a Locales enum value ('fi', 'en-US').
function filenameToLocale(filename: string): Locales {
  const entry = (Object.entries(LocalesToLocaleFileMapping) as [Locales, string][])
    .find(([, v]) => v === filename);
  if (entry) return entry[0];
  if (Object.values(Locales).includes(filename as Locales)) return filename as Locales;
  return Locales.en_US;
}

class Service {
  private _locale: Locales;
  /** Incremented each time i18next finishes loading a language file. */
  private _i18nReady = 0;

  constructor() {
    makeAutoObservable(this, {}, { autoBind: true });
    const saved = PreferencesStore.locale; // e.g. 'fi-FI' from preferences
    this._locale = filenameToLocale(saved);
    this._changeLanguage(LocalesToLocaleFileMapping[this._locale] ?? saved);
  }

  get locale() {
    return this._locale;
  }

  /** Reactive counter — `t()` depends on this so re-renders happen after load. */
  get i18nReady() {
    return this._i18nReady;
  }

  set locale(locale: Locales) {
    if (Object.values(Locales).includes(locale as Locales)) {
      // Update _locale immediately so the language selector reflects the new choice
      this._locale = locale;
      const filename = LocalesToLocaleFileMapping[locale];
      // Persist as i18next filename ('fi-FI') — matches what Settings / backend store
      if (filename) PreferencesStore.locale = filename;
      this._changeLanguage(filename);
    }
  }

  /**
   * Apply a locale from an i18next filename string ('fi-FI', 'en-US') without
   * writing back to PreferencesStore. Used by SessionStore after login.
   */
  applyLocale(filename: string) {
    const locale = filenameToLocale(filename);
    this._locale = locale;
    this._changeLanguage(LocalesToLocaleFileMapping[locale] ?? filename);
  }

  /** Calls i18next.changeLanguage and bumps _i18nReady once the file is loaded. */
  private _changeLanguage(filename: string) {
    i18next.changeLanguage(filename).then(() => {
      runInAction(() => {
        this._i18nReady++;
      });
    });
  }

  get locales() {
    return Object.values(Locales);
  }

  get localesWithNames() {
    return LocaleNames;
  }
}

export const LocalizationService = new Service();
